import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Gavel, Plus, ClockCountdown, WarningOctagon, MapTrifold, ShieldCheck,
  CalendarBlank, ArrowRight,
} from "@phosphor-icons/react";

const TYPES = [
  ["compliance_audit", "Compliance Audits"],
  ["spot_check", "Spot Checks"],
  ["self_certification", "Self-Certifications"],
];
const TYPE_LABEL = { compliance_audit: "Compliance Audit", spot_check: "Spot Check", self_certification: "Self-Certification" };
const REGIONS = ["Texas RE", "WECC", "SERC", "MRO", "RF", "NPCC"];
const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  scoping: "bg-indigo-100 text-indigo-700 border-indigo-300",
  evidence_staging: "bg-amber-100 text-amber-800 border-amber-300",
  active_review: "bg-blue-100 text-blue-700 border-blue-300",
  fieldwork_walkdown: "bg-teal-100 text-teal-800 border-teal-300",
  draft_report: "bg-purple-100 text-purple-700 border-purple-300",
  comment_period: "bg-purple-100 text-purple-700 border-purple-300",
  finalized: "bg-emerald-100 text-emerald-800 border-emerald-300",
  closed: "bg-emerald-100 text-emerald-800 border-emerald-300",
  escalated_to_preliminary_screen: "bg-red-100 text-red-700 border-red-300",
};

const BLANK = {
  title: "", engagement_type: "compliance_audit", audit_sub_type: "scheduled",
  regional_entity: "", lead_cea_auditor: "", notice_date: "", commencement_date: "",
  prior_audit_end_date: "", data_required_date: "", cert_due_date: "", contacts: "",
};

function SummaryCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="border border-border bg-card p-4 flex items-center gap-3">
      <div className={`p-2 ${accent}`}><Icon size={22} weight="bold" /></div>
      <div>
        <div className="text-2xl font-bold leading-none">{value}</div>
        <div className="text-xs text-muted-foreground mt-1">{label}</div>
      </div>
    </div>
  );
}

function NextDue({ c }) {
  if (!c) return <span className="text-xs text-muted-foreground">—</span>;
  const overdue = c.status === "overdue";
  return (
    <span data-testid="eng-next-due" className={`text-xs font-mono font-semibold ${overdue ? "text-red-600" : "text-amber-700"}`}>
      {c.label}: {overdue ? `${Math.abs(c.days_remaining)}d overdue` : `${c.days_remaining}d`}
    </span>
  );
}

export default function Engagements() {
  const nav = useNavigate();
  const [data, setData] = useState({ engagements: [], summary: {} });
  const [tab, setTab] = useState("compliance_audit");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/engagements").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const rows = useMemo(
    () => (data.engagements || []).filter((e) => e.engagement_type === tab),
    [data, tab]);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, contacts: form.contacts.split(",").map((s) => s.trim()).filter(Boolean) };
      const r = await api.post("/engagements", payload);
      toast.success("Engagement created");
      setOpen(false); setForm(BLANK);
      nav(`/engagements/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const s = data.summary || {};
  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="engagements-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 overline text-muted-foreground mb-1">
            <ShieldCheck size={14} /> Compliance Monitoring · NERC ROP Appendix 4C
          </div>
          <h1 className="text-3xl font-bold flex items-center gap-2"><Gavel size={28} /> Engagements</h1>
        </div>
        <Button data-testid="new-engagement-btn" onClick={() => setOpen(true)} className="rounded-none">
          <Plus size={16} className="mr-1" /> New Engagement
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <SummaryCard testid="summary-active" icon={CalendarBlank} label="Active engagements" value={s.active ?? 0} accent="bg-blue-100 text-blue-700" />
        <SummaryCard testid="summary-open-clocks" icon={ClockCountdown} label="Open regulatory clocks" value={s.open_clocks ?? 0} accent="bg-amber-100 text-amber-700" />
        <SummaryCard testid="summary-overdue" icon={WarningOctagon} label="Overdue deadlines" value={s.overdue ?? 0} accent="bg-red-100 text-red-700" />
        <SummaryCard testid="summary-walkdowns" icon={MapTrifold} label="Incomplete walkdowns" value={s.incomplete_walkdowns ?? 0} accent="bg-teal-100 text-teal-700" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none">
          {TYPES.map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`eng-tab-${v}`} className="rounded-none">{l}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="mt-4 border border-border bg-card divide-y divide-border">
        {rows.length === 0 && (
          <div data-testid="eng-empty" className="p-10 text-center text-sm text-muted-foreground">
            No {TYPE_LABEL[tab].toLowerCase()}s yet. Create one to start tracking regulatory clocks.
          </div>
        )}
        {rows.map((e) => (
          <button key={e.id} data-testid={`eng-row-${e.id}`} onClick={() => nav(`/engagements/${e.id}`)}
            className="w-full text-left p-4 hover:bg-secondary flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{e.title}</div>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {e.audit_sub_type && e.engagement_type === "compliance_audit" && (
                  <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{e.audit_sub_type}</span>
                )}
                {e.regional_entity && <span className="text-xs text-muted-foreground">{e.regional_entity}</span>}
                <span className="text-xs text-muted-foreground">· {e.scope_count || 0} scoped req(s)</span>
                <NextDue c={e.next_due} />
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {(e.clock_rollup?.overdue > 0) && (
                <Badge className="rounded-none bg-red-100 text-red-700 border-red-300" variant="outline">
                  {e.clock_rollup.overdue} overdue
                </Badge>
              )}
              <Badge variant="outline" className={`rounded-none ${STATUS_STYLE[e.status] || ""}`}>
                {(e.status || "draft").replace(/_/g, " ")}
              </Badge>
              <ArrowRight size={16} className="text-muted-foreground" />
            </div>
          </button>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-none">
          <DialogHeader><DialogTitle>New Engagement</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Title</Label>
              <Input data-testid="eng-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="rounded-none mt-1" placeholder="e.g. 2026 CIP & O&P Compliance Audit" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.engagement_type} onValueChange={(v) => setForm({ ...form, engagement_type: v })}>
                <SelectTrigger data-testid="eng-form-type" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{TYPE_LABEL[v]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.engagement_type === "compliance_audit" && (
              <div>
                <Label>Audit sub-type</Label>
                <Select value={form.audit_sub_type} onValueChange={(v) => setForm({ ...form, audit_sub_type: v })}>
                  <SelectTrigger data-testid="eng-form-subtype" className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="unscheduled">Unscheduled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Regional Entity (CEA)</Label>
              <Select value={form.regional_entity} onValueChange={(v) => setForm({ ...form, regional_entity: v })}>
                <SelectTrigger data-testid="eng-form-region" className="rounded-none mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{REGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Lead CEA Auditor</Label>
              <Input data-testid="eng-form-lead" value={form.lead_cea_auditor} onChange={(e) => setForm({ ...form, lead_cea_auditor: e.target.value })} className="rounded-none mt-1" />
            </div>
            <div>
              <Label>Notice date</Label>
              <Input data-testid="eng-form-notice" type="date" value={form.notice_date} onChange={(e) => setForm({ ...form, notice_date: e.target.value })} className="rounded-none mt-1" />
            </div>
            {form.engagement_type === "compliance_audit" && (
              <>
                <div>
                  <Label>Commencement date</Label>
                  <Input data-testid="eng-form-commence" type="date" value={form.commencement_date} onChange={(e) => setForm({ ...form, commencement_date: e.target.value })} className="rounded-none mt-1" />
                </div>
                <div>
                  <Label>Prior audit end date</Label>
                  <Input data-testid="eng-form-prior" type="date" value={form.prior_audit_end_date} onChange={(e) => setForm({ ...form, prior_audit_end_date: e.target.value })} className="rounded-none mt-1" />
                </div>
              </>
            )}
            {form.engagement_type === "self_certification" ? (
              <div>
                <Label>Certification due date</Label>
                <Input data-testid="eng-form-certdue" type="date" value={form.cert_due_date} onChange={(e) => setForm({ ...form, cert_due_date: e.target.value })} className="rounded-none mt-1" />
              </div>
            ) : (
              <div>
                <Label>Data required date</Label>
                <Input data-testid="eng-form-datereq" type="date" value={form.data_required_date} onChange={(e) => setForm({ ...form, data_required_date: e.target.value })} className="rounded-none mt-1" />
              </div>
            )}
            <div className="col-span-2">
              <Label>Reminder contacts (comma-separated emails)</Label>
              <Input data-testid="eng-form-contacts" value={form.contacts} onChange={(e) => setForm({ ...form, contacts: e.target.value })}
                className="rounded-none mt-1" placeholder="compliance@entity.coop, sme@entity.coop" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="eng-form-submit" onClick={create} disabled={saving} className="rounded-none">
              {saving ? "Creating…" : "Create Engagement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
